import cn from 'utils/cn'
import Icon from 'core/components/icons/Icon'
import Select from 'core/components/inputs/Select'
import { useSettings } from 'core/hooks/settings/useSettings'
import FundingMethodItem from 'core/widgets/funding-modal/funding-methods/FundingMethodItem'
import { FundingModalFormValues } from 'core/widgets/funding-modal/FundingModal'
import { AnimatePresence, motion } from 'framer-motion'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { ID } from 'types/utility'

const CONNECT_NEW_FUNDING_ACCOUNT_TOKEN_ID = 'connect_new_funding_account'

//  TODO: remove once API integration is done
const fakeAccounts = [
  {
    token_id: '***78',
    label: 'ACME BOFA Debit  ***78'
  },
  {
    token_id: '***22',
    label: 'ACME BOFA Debit  ***22'
  }
]

interface Props {
  fundingMethod: FundingModalFormValues['funding_method']
  onMfaAuthenticated: VoidFunction
}

const AchDebitMethod = ({ fundingMethod, onMfaAuthenticated }: Props) => {
  const {
    set,
    data: { mfa }
  } = useSettings()
  const { watch, setValue } = useFormContext<FundingModalFormValues>()

  const selectedAccountId = watch('account_id')

  const isSelected = fundingMethod === 'ach-debit'

  const options = useMemo(() => {
    return [
      ...fakeAccounts.map((account) => ({
        token_id: account.token_id,
        label: (
          <div className="flex justify-between items-center gap-2">
            <span>{account.label}</span>
            {selectedAccountId === account.token_id && (
              <Icon name="check" color="text-primary" size={15} />
            )}
          </div>
        )
      })),
      {
        token_id: CONNECT_NEW_FUNDING_ACCOUNT_TOKEN_ID,
        label: <div className="text-primary">+Connect new funding account</div>
      }
    ]
  }, [selectedAccountId])

  const selectedAccount = useMemo(
    () =>
      fakeAccounts.find((account) => account.token_id === selectedAccountId),
    [selectedAccountId]
  )

  return (
    <FundingMethodItem
      title="ACH Debit"
      info="May miss July 28th scheduled pay date"
      showCheckIcon={false}
      isSelected={isSelected}
      onClick={() => {
        if (fundingMethod === 'ach-debit') return

        set('mfa', {
          ...mfa,
          action: 'authenticate',
          onSuccess: () => {
            onMfaAuthenticated()
          }
        })
      }}
      children={
        <AnimatePresence initial={false}>
          {isSelected && (
            <motion.section
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={{
                open: { opacity: 1, height: 'auto' },
                collapsed: { opacity: 0, height: 0 }
              }}
              transition={{ duration: 0.2 }}
            >
              <div className="pt-6 flex flex-col gap-6">
                <div className="flex items-center gap-2 text-sm">
                  <span className="bg-primary-1 px-2 py-1 rounded-md">
                    4-5 business days
                  </span>
                  <span className="text-dark-4">
                    May miss July 28th scheduled pay date
                  </span>
                </div>
                <div className="text-dark-3 font-thin">
                  Securely initiate funding request from within the wallet, via
                  Plaid.
                </div>
                <div>
                  <div className="text-dark-3 font-thin mb-1">
                    Pull funds from
                  </div>
                  <Select
                    options={options}
                    className={cn(
                      'w-full [&_input]:leading-tight',
                      '[&_input]:!placeholder-black'
                    )}
                    onChange={(value) => {
                      if (value === CONNECT_NEW_FUNDING_ACCOUNT_TOKEN_ID) {
                        setValue('view', 'add-funding-account')
                      } else {
                        setValue('account_id', value as ID)
                      }
                    }}
                    getDisplayValue={() => {
                      if (selectedAccount) {
                        return selectedAccount.label
                      }
                      return 'Select a funding account'
                    }}
                  />
                </div>
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      }
    />
  )
}

export default AchDebitMethod
